import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import {
	SegmentedControlAppearanceEnum,
	type SegmentedControlOption,
	SegmentedControlSizeEnum,
} from './segmented-control.type';
import { cn } from '@utils';

export type SegmentedControlProps<TValue extends string = string> = {
	options: SegmentedControlOption<TValue>[];
	value: TValue;
	onChange: (value: TValue) => void;
	size?: SegmentedControlSizeEnum;
	appearance?: SegmentedControlAppearanceEnum;
	className?: string;
	itemClassName?: string;
	ariaLabel?: string;
	testId?: string;
};

const DEFAULT_TEST_ID = 'segmented-control';

const containerVariants = cva('inline-flex items-stretch', {
	variants: {
		appearance: {
			[SegmentedControlAppearanceEnum.CONTAINED]: 'gap-1 rounded-full bg-bgPrimary p-1',
			[SegmentedControlAppearanceEnum.CHIPS]: 'gap-2',
		},
	},
	defaultVariants: { appearance: SegmentedControlAppearanceEnum.CONTAINED },
});

const itemVariants = cva(
	'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:text-white',
	{
		variants: {
			size: {
				[SegmentedControlSizeEnum.SMALL]: 'h-7 px-3 text-xs',
				[SegmentedControlSizeEnum.MEDIUM]: 'h-9 px-4 text-sm',
				[SegmentedControlSizeEnum.LARGE]: 'h-11 px-6 text-base',
			},
			appearance: {
				[SegmentedControlAppearanceEnum.CONTAINED]:
					'focus-visible:ring-highlightSelected data-[state=checked]:bg-highlightSelected data-[state=unchecked]:text-textDarkGray data-[state=unchecked]:hover:bg-bgFields',
				[SegmentedControlAppearanceEnum.CHIPS]:
					'border-2 border-transparent focus-visible:ring-accent data-[state=checked]:border-accent data-[state=checked]:bg-primary data-[state=unchecked]:bg-white data-[state=unchecked]:text-textDarkGray data-[state=unchecked]:shadow-sm data-[state=unchecked]:hover:bg-bgPrimary',
			},
		},
		defaultVariants: { size: SegmentedControlSizeEnum.MEDIUM, appearance: SegmentedControlAppearanceEnum.CONTAINED },
	}
);

const SegmentedControl = <TValue extends string = string>({
	options,
	value,
	onChange,
	size = SegmentedControlSizeEnum.MEDIUM,
	appearance = SegmentedControlAppearanceEnum.CONTAINED,
	className,
	itemClassName,
	ariaLabel,
	testId = DEFAULT_TEST_ID,
}: SegmentedControlProps<TValue>): JSX.Element => {
	function handleValueChange(next: string): void {
		if (!next) {
			return;
		}
		onChange(next as TValue);
	}

	return (
		<RadioGroupPrimitive.Root
			value={value}
			onValueChange={handleValueChange}
			orientation="horizontal"
			className={cn(containerVariants({ appearance }), className)}
			aria-label={ariaLabel}
			data-testid={testId}
		>
			{options.map(option => (
				<RadioGroupPrimitive.Item
					key={option.value}
					value={option.value}
					disabled={option.disabled}
					aria-label={option.ariaLabel}
					className={cn(itemVariants({ size, appearance }), itemClassName)}
					data-testid={`${testId}-${option.value}`}
				>
					{option.icon ? <span className="inline-flex shrink-0 items-center">{option.icon}</span> : null}
					{option.label ? <span>{option.label}</span> : null}
				</RadioGroupPrimitive.Item>
			))}
		</RadioGroupPrimitive.Root>
	);
};

export { SegmentedControl };
